import React, { useRef, useState } from 'react';
import { Trash2, Upload } from 'lucide-react';
import { TaskyButton, TaskyFormGroup } from '../tasky';
import adminVideoTemplateService from '../../services/api/adminVideoTemplateService';

interface VideoTemplateThumbnailFieldProps {
  /** thumbnail_url の値（アップロードで得た CDN の URL、または以前に手入力された URL） */
  value: string;
  onChange: (url: string) => void;
  onUploadingChange?: (uploading: boolean) => void;
  disabled?: boolean;
}

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * 動画スタイルのサムネイル画像。利用者の「動画スタイルを選ぶ」一覧のカードに表示される
 * （サンプル動画があるスタイルでは再生前の静止画として使う）。
 */
const VideoTemplateThumbnailField: React.FC<VideoTemplateThumbnailFieldProps> = ({
  value,
  onChange,
  onUploadingChange,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setBusy = (busy: boolean) => {
    setUploading(busy);
    onUploadingChange?.(busy);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('JPEG / PNG / WebP の画像を選んでください');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError('画像は5MB以内にしてください');
      return;
    }

    setError(null);
    setBusy(true);
    try {
      const uploaded = await adminVideoTemplateService.uploadSample('thumbnail', file, file.name);
      onChange(uploaded.url);
    } catch {
      setError('アップロードに失敗しました。時間をおいて再度お試しください');
    } finally {
      setBusy(false);
    }
  };

  const pickButton = (label: string) => (
    <TaskyButton
      type="button"
      variant="secondary"
      size="sm"
      onClick={() => inputRef.current?.click()}
      disabled={disabled || uploading}
    >
      <Upload className="h-4 w-4" />
      {uploading ? 'アップロード中…' : label}
    </TaskyButton>
  );

  return (
    <TaskyFormGroup
      label="サムネイル"
      htmlFor="vt-thumbnail_file"
      error={error || undefined}
      helper="利用者の「動画スタイルを選ぶ」一覧のカードに表示されます（JPEG / PNG / WebP、5MBまで。横長16:10推奨）"
    >
      <input
        ref={inputRef}
        id="vt-thumbnail_file"
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        className="hidden"
        onChange={handleFile}
        disabled={disabled || uploading}
      />
      {value ? (
        <div className="space-y-2">
          <img
            src={value}
            alt="サムネイルのプレビュー"
            className="aspect-[16/10] w-full max-w-sm rounded-lg border border-warm-border bg-gray-50 object-cover"
          />
          <div className="flex gap-2">
            {pickButton('差し替える')}
            <TaskyButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onChange('')}
              disabled={disabled || uploading}
            >
              <Trash2 className="h-4 w-4" />
              外す
            </TaskyButton>
          </div>
        </div>
      ) : (
        pickButton('画像を選ぶ')
      )}
    </TaskyFormGroup>
  );
};

export default VideoTemplateThumbnailField;
